/**
 * artifiPreviewCache — LRU-bounded cache of resolved Artifi preview URLs.
 *
 * Demo Sprint Day 2 work for the persistent Stage's reactive preview loop.
 * The Stage resolves an Artifi preview URL for a given
 * (product, color, view, variant-binding) combination; resolution is
 * expensive (network round-trip + Artifi render), so we cache the result
 * keyed by a stable fingerprint and serve repeat selections instantly.
 *
 * Cache key shape (per Q2 follow-up decision):
 *   `${productSku}|${colorSku}|${viewCode}|${variantBindingFingerprint}|${artworkFileId}|${campaignProductId}`
 *
 * variantBindingFingerprint is a stable hash of sorted
 * (variantKey, artworkFileId + colorwayId) pairs -- see `_bindingSlot`, which
 * also documents why the colorway belongs in this slot and still accepts the
 * pre-colorway bare-id shape. Routing changes that don't change the PICTURE
 * on the garment don't invalidate (e.g., re-routing color X from variant B to
 * variant A where both have the same artwork AND the same colorway = cache
 * hit); rebinding a colorway does, because the same stitch file renders in
 * different threads.
 *
 * Bounded at CACHE_MAX_ENTRIES (30) entries with LRU eviction — the Map's
 * insertion order is the LRU order; get() touches an entry by re-inserting
 * it. Stored in-memory and ephemeral: lost on page reload. Fine for the
 * demo and MVP — re-hydration from a backing store is a post-MVP question.
 */
import { defineStore } from "pinia";
import { ref } from "vue";

export const CACHE_MAX_ENTRIES = 30;

/**
 * Build the canonical cache key for a (product, color, view, bindings) tuple.
 * Null/undefined slots collapse to empty string so the shape stays
 * `a|b|c|d|e|f` regardless of input.
 *
 * Single-artwork axis (issue #2225 -- cross-product preview bleed): the
 * bare single-artwork path passes ``artworkFileId`` (the file rendered onto
 * the garment) but NOT ``campaignProductId`` (that is only set on the
 * multi-placement composite path). Without ``artworkFileId`` in the key, a
 * with-artwork render and a DIFFERENT product's bare render on the same
 * (product, color, view) collapse to the same key and serve each other's
 * image (blank Product B showing Product A's composited preview). Fold
 * ``artworkFileId`` into its own slot so a with-artwork render, a bare render
 * (artworkFileId null), and two renders with different artwork never collide.
 *
 * Multi-placement composite (card https://trello.com/c/pI48s0zf): when
 * ``campaignProductId`` is present the render is a COMPOSITE of every
 * placement on the active view's group, not a single artwork. Fold it into the
 * key so a composite and a single-artwork render on the same
 * (product, color, view) never collide, and two composites on different
 * campaign-products key distinctly. Absent -> empty slot -> the single-artwork
 * key shape is unchanged (no cache-invalidation churn).
 */
/**
 * Render one variant's binding into its fingerprint slot.
 *
 * Two accepted shapes, and back-compat is the reason for both:
 *
 *   * a bare artwork file id (the original shape) -> `art-1`
 *   * `{artworkFileId, colorwayId}` (colorway axis) -> `art-1@11`
 *
 * THE COLORWAY BELONGS IN THIS SLOT, not in a seventh positional segment.
 * The fingerprint answers exactly one question -- "does this variant put a
 * different picture on the garment?" -- which is why re-routing a colour
 * between two variants holding the SAME artwork is deliberately still a cache
 * hit. Rebinding a colorway changes the thread colours the stitch file is
 * rendered in, so Artifi returns a different composite for the same artwork
 * file: same question, one more input to it.
 *
 * Without this, a rebind left product / colour / view / artworkFileId all
 * unchanged, produced a byte-identical key, and the LRU served the PREVIOUS
 * colorway's composite. Nothing errored -- the preview was simply, silently
 * wrong (card https://trello.com/c/vc8U5340).
 *
 * An unbound variant emits the bare id with no `@` suffix rather than
 * `art-1@`, so keys minted before the colorway axis existed still match and
 * the whole cache does not cold-start on deploy.
 */
function _bindingSlot(binding) {
    if (binding == null) return "";
    if (typeof binding !== "object") return String(binding);
    const file = binding.artworkFileId ?? "";
    const colorway = binding.colorwayId;
    return colorway == null ? `${file}` : `${file}@${colorway}`;
}

export function buildCacheKey({
    productSku,
    colorSku,
    viewCode,
    variantBindings,
    artworkFileId,
    campaignProductId,
}) {
    const fingerprint = Object.entries(variantBindings ?? {})
        .map(([k, v]) => `${k}:${_bindingSlot(v)}`)
        .sort()
        .join(",");
    return `${productSku ?? ""}|${colorSku ?? ""}|${viewCode ?? ""}|${fingerprint}|${artworkFileId ?? ""}|${campaignProductId ?? ""}`;
}

export const useArtifiPreviewCacheStore = defineStore("artifiPreviewCache", () => {
    // Map preserves insertion order; that's what gives us cheap LRU semantics.
    const entries = ref(new Map());

    function has(key) {
        return entries.value.has(key);
    }

    function get(key) {
        if (!entries.value.has(key)) return null;
        // Touch: re-insert to move to most-recent end of insertion order.
        const url = entries.value.get(key);
        entries.value.delete(key);
        entries.value.set(key, url);
        return url;
    }

    function set(key, url) {
        if (url == null) return;
        if (entries.value.has(key)) {
            entries.value.delete(key);
        } else if (entries.value.size >= CACHE_MAX_ENTRIES) {
            // Evict the oldest entry (first in insertion order).
            const oldestKey = entries.value.keys().next().value;
            if (oldestKey != null) entries.value.delete(oldestKey);
        }
        entries.value.set(key, url);
    }

    function clear() {
        entries.value.clear();
    }

    function size() {
        return entries.value.size;
    }

    /**
     * Delete a specific entry. Returns true if the entry existed and was
     * removed, false otherwise. Used by `useArtifiPreviewCache` (the
     * composable wrapper) when refresh() needs to force a re-fetch by
     * evicting the cached URL for the current key.
     */
    function del(key) {
        return entries.value.delete(key);
    }

    return {
        entries,
        has,
        get,
        set,
        clear,
        size,
        delete: del,
    };
});
